import { Link } from "react-router-dom";
import { Instagram, Facebook, ArrowUpRight } from "lucide-react";
import { useLang, CONTACT, whatsappLink } from "../i18n";
import { PRODUCTS, productHref } from "../data/products";
import { WhatsAppIcon } from "./WhatsAppButton";

export default function Footer() {
  const { t, lang } = useLang();
  const linkCls = "text-sm font-light text-[#A39E93] transition-colors duration-300 hover:text-[#F3EFE6]";
  const headCls = "text-[10px] uppercase tracking-[0.3em] font-mono text-[#C8A86B]";

  return (
    <footer data-testid="site-footer" className="relative overflow-hidden border-t border-[#1C1A17] bg-[#060606]">
      <div className="absolute inset-0 bronze-glow opacity-70" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 sm:pt-28">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10">
          <div>
            <p className={headCls}>{t.footer_claim}</p>
            <p className="mt-6 font-serif font-light leading-[0.95] tracking-tight text-[#F3EFE6] text-[13vw] sm:text-[9vw] lg:text-[7vw]">
              Arte <span className="italic text-[#C8A86B]">&</span> Infissi
            </p>
          </div>
          <a
            href={whatsappLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-whatsapp-cta"
            className="group inline-flex w-fit items-center gap-3 border border-[#C8A86B] px-7 py-4 text-xs uppercase tracking-[0.2em] text-[#C8A86B] transition-all duration-300 hover:bg-[#C8A86B] hover:text-[#080808]"
          >
            <WhatsAppIcon size={16} /> {t.whatsapp_label}
            <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </div>

        <div className="hairline mt-14" />

        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 py-14">
          <div>
            <p className={headCls}>{t.footer_products}</p>
            <div className="mt-5 flex flex-col gap-3">
              {PRODUCTS.map((p) => (
                <Link key={p.slug} to={productHref(p)} data-testid={`footer-product-${p.slug}`} className={linkCls}>
                  {lang === "it" ? p.name_it : p.name_en}
                </Link>
              ))}
            </div>
          </div>
          <div>
            <p className={headCls}>{t.footer_nav}</p>
            <div className="mt-5 flex flex-col gap-3">
              <Link to="/projects" data-testid="footer-link-projects" className={linkCls}>{t.nav_projects}</Link>
              <Link to="/about" data-testid="footer-link-about" className={linkCls}>{t.nav_about}</Link>
              <Link to="/contact" data-testid="footer-link-contact" className={linkCls}>{t.nav_contact}</Link>
              <Link to="/admin/login" data-testid="footer-link-admin" className="text-sm font-light text-[#666157] transition-colors hover:text-[#A39E93]">{t.nav_admin}</Link>
            </div>
          </div>
          <div className="col-span-2 md:col-span-1">
            <p className={headCls}>{t.footer_contact}</p>
            <div className="mt-5 flex flex-col gap-3 text-sm font-light text-[#A39E93]">
              <p>{CONTACT.factory}</p>
              <p className="text-[#827D72]">{CONTACT.hq}</p>
              <a data-testid="footer-phone" href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className="hover:text-[#F3EFE6] transition-colors">{CONTACT.phone}</a>
              <a data-testid="footer-email" href={`mailto:${CONTACT.email}`} className="hover:text-[#F3EFE6] transition-colors">{CONTACT.email}</a>
            </div>
          </div>
          <div className="col-span-2 md:col-span-1">
            <p className={headCls}>{t.contact_social_label}</p>
            <div className="mt-5 flex gap-3">
              <a href={CONTACT.instagram} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" aria-label="Instagram" className="flex h-11 w-11 items-center justify-center border border-[#262420] text-[#A39E93] transition-all duration-300 hover:border-[#C8A86B] hover:text-[#C8A86B]"><Instagram size={16} /></a>
              <a href={CONTACT.facebook} target="_blank" rel="noopener noreferrer" data-testid="footer-facebook" aria-label="Facebook" className="flex h-11 w-11 items-center justify-center border border-[#262420] text-[#A39E93] transition-all duration-300 hover:border-[#C8A86B] hover:text-[#C8A86B]"><Facebook size={16} /></a>
            </div>
            <p className="mt-6 text-xs font-light leading-relaxed text-[#827D72]">{t.contact_hours}</p>
          </div>
        </div>
      </div>
      <div className="relative border-t border-[#1C1A17]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row justify-between gap-2">
          <p className="text-[11px] font-mono text-[#666157]">© {new Date().getFullYear()} {CONTACT.company} — Cerveteri (RM). {t.footer_rights}</p>
          <p className="text-[11px] font-mono text-[#666157]">{CONTACT.coordinates}</p>
        </div>
      </div>
    </footer>
  );
}
